import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Paper,
  Popper,
  InputAdornment,
  Fade,
  Radio,
  List,
  ListItem,
  ClickAwayListener
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// 🎨 Modern SaaS Styled Display Field
const StyledDisplayField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease-in-out',
    height: 36, // Reduced height for more compact design
    minHeight: 36,
    fontFamily: theme.typography.fontFamily,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    backdropFilter: 'blur(8px)',
    
    '&:hover': {
      borderColor: theme.palette.primary.light,
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(255, 255, 255, 1)',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20, 0 2px 8px rgba(0, 0, 0, 0.12)`,
      transform: 'translateY(-1px)',
    },
    
    '& fieldset': {
      border: 'none',
    },
    
    '& input': {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.text.primary,
      fontFamily: 'inherit',
      padding: '8px 12px !important',
      cursor: 'pointer !important',
      caretColor: 'transparent',
      lineHeight: 1.2,
      
      '&::placeholder': {
        color: theme.palette.text.secondary,
        fontWeight: 400,
        fontSize: '13px',
      },
    },
  },
}));

// 🎨 Modern SaaS Dropdown Paper
const StyledDropdownPaper = styled(Paper)(({ theme }) => ({
  marginTop: 8,
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 12px 40px rgba(0, 0, 0, 0.12)',
  overflow: 'hidden',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  fontFamily: theme.typography.fontFamily,
  minWidth: '300px',
  maxHeight: '360px',
  
  // Search header with gradient
  '& .search-header': {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  },
}));

// 🎨 Modern SaaS Options List
const StyledOptionsList = styled(List)(({ theme }) => ({
  padding: '8px',
  maxHeight: 280,
  overflowY: 'auto',
  
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[300],
    borderRadius: '3px',
  },
  
  '& .MuiListItem-root': {
    padding: '10px 12px',
    margin: '2px 0',
    borderRadius: theme.shape.borderRadius,
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(4px)',
    },
    
    '&.selected': {
      backgroundColor: `${theme.palette.primary.main}10`,
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      paddingLeft: '9px',
      
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}15`,
      },
      
      '& .MuiRadio-root': {
        color: theme.palette.primary.main,
      },
    },
  },
}));

// 🎨 Modern SaaS Search Field
const StyledSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '14px',
    fontFamily: theme.typography.fontFamily,
    height: 40,
    transition: 'all 0.2s ease-in-out',
    
    '&:hover': {
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.background.paper,
    },
    
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
    
    '& fieldset': {
      border: 'none',
    },
    
    '& input': {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      
      '&::placeholder': {
        color: theme.palette.text.secondary,
        fontWeight: 400,
        fontSize: '13px',
      },
    },
  },
}));

const BusinessDropdown = ({
  businesses = [],
  selectedBusiness = null,
  onBusinessSelect,
  loading = false,
  placeholder = "Unternehmen auswählen...",
  width = 320,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter businesses based on search value - search across multiple fields
  const filteredBusinesses = React.useMemo(() => {
    const allBusinesses = businesses || [];
    if (!searchValue) return allBusinesses;

    const searchTerm = searchValue.toLowerCase();
    return allBusinesses.filter(business => {
      const businessName = (business.businessName || '').toLowerCase();
      const city = (business.address?.city || '').toLowerCase();
      const locationId = (business.locationId || business.googleLocationId || '').toString().toLowerCase();
      const cloudTenantId = (business.contactCloudTenantId || '').toString().toLowerCase();
      const email = (business.email || '').toLowerCase();

      return (
        businessName.includes(searchTerm) ||
        city.includes(searchTerm) ||
        locationId.includes(searchTerm) ||
        cloudTenantId.includes(searchTerm) ||
        email.includes(searchTerm)
      );
    });
  }, [businesses, searchValue]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchValue('');
    } else {
      // Focus search field when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  console.log('🔍 BusinessDropdown Debug:', {
    totalBusinesses: filteredBusinesses.length,
    selectedBusiness: selectedBusiness?.businessName,
    searchValue,
    open
  });

  const handleToggleDropdown = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const handleSelectBusiness = (business) => {
    if (business !== selectedBusiness) {
      onBusinessSelect(business);
    }
    setOpen(false);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  // Display value for the main field
  const displayValue = selectedBusiness ? selectedBusiness.businessName || '' : '';

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} sx={{ width }}>
        {/* Main Display Field */}
        <StyledDisplayField
          fullWidth
          value={displayValue}
          placeholder={placeholder}
          variant="outlined"
          onClick={handleToggleDropdown}
          disabled={disabled}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDownIcon
                  sx={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.1s ease',
                    color: '#8a8d91',
                    fontSize: '16px',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />

        {/* Custom Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 9999, width: anchorRef.current?.offsetWidth || width }}
          disablePortal={true}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                padding: 8,
              },
            },
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'offset',
              enabled: true,
              options: {
                offset: [0, 4],
              },
            },
          ]}
        >
          <Fade in={open} timeout={200}>
            <StyledDropdownPaper>
              {/* Search Field */}
              <Box className="search-header">
                <StyledSearchField
                  fullWidth
                  size="small"
                  placeholder="Nach Google-Konten suchen"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  inputRef={searchInputRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#8a8d91', fontSize: '16px' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Options List */}
              <StyledOptionsList>
                {loading ? (
                  <ListItem>
                    <Typography sx={{ color: '#65676b', fontSize: '14px', fontStyle: 'italic' }}>
                      Lade Unternehmen...
                    </Typography>
                  </ListItem>
                ) : filteredBusinesses.length === 0 ? (
                  <ListItem>
                    <Typography sx={{ color: '#65676b', fontSize: '14px', fontStyle: 'italic' }}>
                      {searchValue ? 'Keine Treffer gefunden' : 'Keine Unternehmen verfügbar'}
                    </Typography>
                  </ListItem>
                ) : (
                  filteredBusinesses.map((business) => {
                    const isSelected = selectedBusiness?.id === business.id;
                    
                    return (
                      <ListItem
                        key={business.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => handleSelectBusiness(business)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          width: '100%',
                          fontFamily: 'Helvetica, Arial, sans-serif',
                        }}
                      >
                        <Radio
                          checked={isSelected}
                          size="small"
                          sx={{
                            padding: '4px 8px 4px 0',
                            color: 'text.secondary',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                        
                        <Box sx={{ flex: 1, minWidth: 0, py: '2px' }}>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: isSelected ? 500 : 400,
                              color: '#1c1e21',
                              lineHeight: 1.4,
                              fontFamily: 'inherit',
                              mb: 0.25,
                            }}
                            noWrap
                          >
                            {business.businessName || 'Unbekanntes Unternehmen'}
                          </Typography>
                          
                          {business.address?.city && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: '#65676b',
                                fontFamily: 'inherit',
                                lineHeight: 1.2,
                              }}
                              noWrap
                            >
                              {business.address.city}
                              {business.address.postalCode && ` · ${business.address.postalCode}`}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })
                )}
              </StyledOptionsList>
            </StyledDropdownPaper>
          </Fade>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default BusinessDropdown;